import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { IAutoCompleteItem } from 'auto-complete-lib';
import { forkJoin, Observable, of } from 'rxjs';
import { IMultiselectListItem } from 'multi-select-autocomplete-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { BhEntityFieldMetaData, BullhornEntityFiltersService } from './bullhorn.entity-filters.service';

type OperatorOptionMetaData = { id: string; name: string };

interface FilterRow {
  id: string;
  formGroup: UntypedFormGroup;
  multiValueInputControl: UntypedFormControl; // For autocomplete input
  isAddingValue: boolean; // For visual feedback when adding values
}

enum OperatorOption {
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  INCLUDES = 'INCLUDES',
  NOT_INCLUDES = 'NOT_INCLUDES',
}

@Component({
  selector: 'app-bullhorn-entity-filters',
  templateUrl: './bullhorn.entity-filters.component.html',
  styleUrls: ['./bullhorn.entity-filters.component.scss'],
  standalone: false,
})
export class BullhornEntityFiltersComponent implements OnInit {
  private static readonly FILTER_TYPE = 'ENTITY';

  /*
    NOTE: { entityPath, filterValues, operator, filterType }
  */
  @Input()
  filtersFormArray: UntypedFormArray;

  /*
    Filter rows to display in the UI, each containing 'field', 'operator', and 'value' fields
  */
  filterRows: FilterRow[] = [];

  /*
    Metadata
  */
  companyFields: BhEntityFieldMetaData[] = [];
  operatorOptions: OperatorOptionMetaData[] = [
    { id: OperatorOption.EQUALS, name: 'equals' },
    { id: OperatorOption.NOT_EQUALS, name: 'does not equal' },
    { id: OperatorOption.INCLUDES, name: 'includes any of' },
    { id: OperatorOption.NOT_INCLUDES, name: 'does not include any of' },
  ];

  loading = false;

  constructor(protected service: BullhornEntityFiltersService, protected messageNotificationService: MessageNotificationService) { }

  ngOnInit() {
    this.loading = true;

    forkJoin({
      fields: this.service.getEntityFields(),
    }).subscribe({
      next: (data) => {
        this.companyFields = data.fields;

        if (this.filtersFormArray.length) {
          this.loadExistingFilterRows();
        }

        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        console.error('Error loading company restrictions:', error);
        this.messageNotificationService.error('Error loading company restrictions', error);
      },
    });
  }

  addFilterRow(): void {
    const rowId = this.generateRowId();

    const filterRowFormGroup = new UntypedFormGroup({
      field: new UntypedFormControl(null, [Validators.required]),
      operator: new UntypedFormControl(null, [Validators.required]),
      value: new UntypedFormControl(null, [BullhornEntityFiltersComponent.valueRequiredValidator]),
    });

    const newRow: FilterRow = {
      id: rowId,
      formGroup: filterRowFormGroup,
      multiValueInputControl: new UntypedFormControl(''),
      isAddingValue: false,
    };

    this.filterRows.push(newRow);

    // Add to filtersFormArray with the expected structure
    const filterFormControl = new UntypedFormGroup({
      filterId: new UntypedFormControl(rowId),
      entityPath: new UntypedFormControl(null, [Validators.required]),
      filterValues: new UntypedFormControl(null, [BullhornEntityFiltersComponent.filterValuesRequiredValidator]),
      operator: new UntypedFormControl(null, [Validators.required]),
      filterType: new UntypedFormControl(BullhornEntityFiltersComponent.FILTER_TYPE),
    });

    this.filtersFormArray.push(filterFormControl);
  }

  removeFilterRow(index: number): void {
    if (this.filterRows.length <= 0 || index < 0 || index >= this.filterRows.length) {
      return;
    }

    const filterRowToRemove = this.filterRows[index];

    // Find the form group in filtersFormArray by filterId to remove it
    const formArrayIndex = this.filtersFormArray.controls.findIndex(
      (control) => (control as UntypedFormGroup).get('filterId')?.value === filterRowToRemove.id
    );
    if (formArrayIndex >= 0) {
      this.filtersFormArray.removeAt(formArrayIndex);
      this.filtersFormArray.markAsDirty();
    }

    // Remove from UI filter rows
    this.filterRows.splice(index, 1);
  }

  trackByIndex(index: number, item: FilterRow): any {
    return item.id;
  }

  //#region Fields
  entityFieldsFilterFn(filterValue: string): Observable<IAutoCompleteItem<BhEntityFieldMetaData>[]> {
    const lowerCaseFilter = (filterValue || '').toLowerCase();
    const result = this.companyFields
      .filter((item) => item.name.toLowerCase().includes(lowerCaseFilter))
      .map((item) => ({ label: item.name, data: item }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return of(result);
  }

  onFieldSelected(selectedItem: BhEntityFieldMetaData, filterRow: FilterRow): void {
    if (selectedItem.id === filterRow.formGroup.get('field')?.value) {
      return;
    }

    const operatorControl = filterRow.formGroup.get('operator');

    filterRow.formGroup.patchValue({
      field: selectedItem.id,
    });

    // Auto-select first operator if no operator is currently selected
    if (!operatorControl?.value && this.operatorOptions.length > 0) {
      filterRow.formGroup.patchValue({
        operator: this.operatorOptions[0].id,
      });
    }

    this.resetValueControlValue(filterRow);
    this.syncFilterRowToFormArray(filterRow);
  }

  getSelectedFieldName(filterRow: FilterRow): string {
    const fieldControl = filterRow.formGroup.get('field');
    return this.companyFields.find((f) => f.id === fieldControl?.value)?.name || '';
  }

  onFieldInputCleared(filterRow: FilterRow) {
    filterRow.formGroup.patchValue({
      field: null,
      value: null,
    });
    this.syncFilterRowToFormArray(filterRow);
  }

  getSelectedField(filterRow: FilterRow): BhEntityFieldMetaData | undefined {
    const fieldControl = filterRow.formGroup.get('field');
    return this.companyFields.find((f) => f.id === fieldControl?.value);
  }

  hasFieldSelected(filterRow: FilterRow): boolean {
    return !!filterRow.formGroup.get('field')?.value;
  }

  getFieldType(filterRow: FilterRow): string {
    const selectedField = this.getSelectedField(filterRow);
    if (!selectedField) return 'disabled';

    return this.normalizeFieldType(selectedField);
  }

  getFieldOptions(filterRow: FilterRow): any[] {
    const selectedField = this.getSelectedField(filterRow);
    return selectedField?.options || [];
  }

  getFieldOptionsWithSelectedFirst(filterRow: FilterRow): any[] {
    const selectedField = this.getSelectedField(filterRow);
    const allOptions = selectedField?.options || [];

    if (this.isMultiValueOperator(filterRow)) {
      const valueControl = filterRow.formGroup.get('value');
      const selectedValues = valueControl?.value || [];

      if (Array.isArray(selectedValues) && selectedValues.length > 0) {
        const selectedOptions = allOptions.filter((option) => selectedValues.includes(option.value));
        const unselectedOptions = allOptions.filter((option) => !selectedValues.includes(option.value));

        return [...selectedOptions, ...unselectedOptions];
      }
    }

    return allOptions;
  }

  getFieldOptionLabel(filterRow: FilterRow, value: any): string {
    const options = this.getFieldOptions(filterRow);
    const matchedOption = options.find((opt) => opt.value === value);
    return matchedOption ? matchedOption.label : '';
  }

  getCurrentFieldOptionLabel(filterRow: FilterRow): string {
    const valueControl = filterRow.formGroup.get('value');
    const currentValue = valueControl?.value;
    return this.getFieldOptionLabel(filterRow, currentValue);
  }

  isMultiValueOperator(filterRow: FilterRow): boolean {
    const operatorControl = filterRow.formGroup.get('operator');
    const operatorValue = operatorControl?.value;
    return operatorValue === OperatorOption.INCLUDES || operatorValue === OperatorOption.NOT_INCLUDES;
  }

  getSelectedOperator(filterRow: FilterRow): OperatorOptionMetaData | null {
    const operatorControl = filterRow.formGroup.get('operator');
    const operatorValue = operatorControl?.value;
    return this.operatorOptions.find((option) => option.id === operatorValue) || null;
  }

  isOperatorMultiValue(filterRow: FilterRow): boolean {
    const selectedOperator = this.getSelectedOperator(filterRow);
    return selectedOperator?.id === OperatorOption.INCLUDES || selectedOperator?.id === OperatorOption.NOT_INCLUDES;
  }

  onRelationshipSelectionChange(event: IMultiselectListItem[], filterRow: FilterRow): void {
    if (event && event.length) {
      const selectedIds = event.map((item) => item.id);
      filterRow.formGroup.patchValue({ value: selectedIds });
    } else {
      filterRow.formGroup.patchValue({ value: null });
    }

    this.syncFilterRowToFormArray(filterRow);
  }

  private normalizeFieldType(field: BhEntityFieldMetaData): string {
    if (!field) return 'disabled';

    if (field.options && field.options.length > 0) {
      return 'select';
    }

    if (this.service.relationshipFieldNames.some((f) => f === field.id)) {
      return 'relationship';
    }

    const dataType = field.dataType?.toLowerCase() || '';

    if (dataType === 'integer' || dataType === 'double' || dataType === 'bigdecimal') {
      return 'number';
    }

    if (dataType === 'string') {
      return 'text';
    }

    // Fallback to text for unknown types
    return 'text';
  }
  //#endregion

  //#region Operators
  onOperatorSelected(event, filterRow: FilterRow): void {
    const previousOperator = filterRow.formGroup.get('operator')?.value;
    const newOperator = event.value.id;

    // Check if we're switching between single and multi-value modes
    const wasPreviouslyMultiValue = previousOperator === OperatorOption.INCLUDES || previousOperator === OperatorOption.NOT_INCLUDES;
    const isNowMultiValue = newOperator === OperatorOption.INCLUDES || newOperator === OperatorOption.NOT_INCLUDES;

    filterRow.formGroup.patchValue({
      operator: newOperator,
    });

    // Handle value conversion when switching between single/multi modes
    if (wasPreviouslyMultiValue !== isNowMultiValue) {
      this.handleOperatorModeChange(filterRow, isNowMultiValue);
    }

    this.syncFilterRowToFormArray(filterRow);
  }

  private handleOperatorModeChange(filterRow: FilterRow, isNowMultiValue: boolean): void {
    const valueControl = filterRow.formGroup.get('value');
    const currentValue = valueControl?.value;

    if (isNowMultiValue) {
      // Switching to multi-value mode
      if (currentValue && !Array.isArray(currentValue)) {
        // Convert single value to array
        filterRow.formGroup.patchValue({ value: [currentValue] });
      } else if (!currentValue) {
        filterRow.formGroup.patchValue({ value: [] });
      }
    } else {
      // Switching to single-value mode
      if (Array.isArray(currentValue)) {
        // Take the first value from array or null if empt
        const singleValue = currentValue.length > 0 ? currentValue[0] : null;
        // Wait for the mat-select component to change to single-value mode before patching
        setTimeout(() => {
          filterRow.formGroup.patchValue({ value: singleValue });
        }, 0);
      }
    }
  }
  //#endregion

  //#region Values
  getInputPlaceholder(filterRow: FilterRow): string {
    const selectedField = this.getSelectedField(filterRow);
    const fieldType = this.getFieldType(filterRow);

    if (!selectedField) return 'Select a field first...';

    if (fieldType === 'number' && this.service.percentageFields.includes(selectedField.id)) {
      return 'Enter a percentage (e.g., 15.5)...';
    }

    switch (fieldType) {
      case 'text':
        return 'Type text...';
      case 'number':
        return 'Enter number...';
      case 'relationship':
        return `Search ${selectedField.optionsType || 'items'}...`;
      default:
        return 'Enter value...';
    }
  }

  getMultiValues(filterRow: FilterRow): any[] {
    const valueControl = filterRow.formGroup.get('value');
    const currentValue = valueControl?.value;

    if (Array.isArray(currentValue)) {
      return currentValue;
    }

    return currentValue ? [currentValue] : [];
  }

  onMultiSelectChanged(filterRow: FilterRow): void {
    this.syncFilterRowToFormArray(filterRow);
  }

  getMultiValueFormControl(filterRow: FilterRow): UntypedFormControl {
    return filterRow.multiValueInputControl;
  }

  getMultiValueInputPlaceholder(filterRow: FilterRow): string {
    const selectedField = this.getSelectedField(filterRow);
    const fieldType = this.getFieldType(filterRow);
    const values = this.getMultiValues(filterRow);

    if (!selectedField) return 'Select a field first...';

    if (values.length === 0) {
      switch (fieldType) {
        case 'text':
          return 'Search or add your own...';
        case 'number':
          return 'Search or add your own...';
        default:
          return 'Search or add your own...';
      }
    } else if (values.length === 1) {
      return values[0].toString();
    } else {
      const formattedArray = values
        .slice(0, -1)
        .map((v) => v.toString())
        .join(', ');
      const lastElement = values[values.length - 1].toString();
      return `${formattedArray} and ${lastElement}`;
    }
  }

  addMultiValueFromInput(filterRow: FilterRow): void {
    const inputControl = filterRow.multiValueInputControl;
    const value = inputControl.value;

    if (!value || (typeof value === 'string' && !value.trim())) {
      return;
    }

    filterRow.isAddingValue = true;
    setTimeout(() => {
      filterRow.isAddingValue = false;
    }, 500);

    const fieldType = this.getFieldType(filterRow);
    const isPercentage = this.isPercentageField(filterRow);
    let processedValue = value;

    // Process value based on field type
    if (fieldType === 'number') {
      processedValue = parseFloat(value.toString());
      if (isNaN(processedValue as number)) {
        return;
      }
      // For percentage fields, limit to 4 decimal places for display
      if (isPercentage) {
        processedValue = Math.round(processedValue * 10000) / 10000;
      }
    } else if (fieldType === 'text') {
      processedValue = value.toString().trim();
    }

    const currentValues = this.getMultiValues(filterRow);

    // Check if value already exists
    if (!currentValues.includes(processedValue)) {
      const newValues = [...currentValues, processedValue];
      filterRow.formGroup.patchValue({ value: newValues });
      this.syncFilterRowToFormArray(filterRow);
    }

    // Clear input
    inputControl.setValue('');
  }

  clearMultiValueInput(filterRow: FilterRow): void {
    filterRow.multiValueInputControl.setValue('');
  }

  isAddingValue(filterRow: FilterRow): boolean {
    return filterRow.isAddingValue;
  }

  stayOpen(event: Event): void {
    event.stopPropagation();
  }

  removeMultiValue(filterRow: FilterRow, index: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    const currentValues = this.getMultiValues(filterRow);
    if (index >= 0 && index < currentValues.length) {
      const newValues = currentValues.filter((_, i) => i !== index);
      filterRow.formGroup.patchValue({
        value: newValues.length > 0 ? newValues : null,
      });
      this.syncFilterRowToFormArray(filterRow);
    }
  }

  getRelationshipInitialValues(filterRow: FilterRow): IMultiselectListItem[] {
    const valueControl = filterRow.formGroup.get('value');
    const currentValue: number[] = valueControl?.value; // A list of IDs

    if (!currentValue || !currentValue.length) {
      return [];
    }

    return currentValue.map((item) => ({
      id: item,
      label: '',
    }));
  }

  private isPercentageField(filterRow: FilterRow): boolean {
    const selectedField = this.getSelectedField(filterRow);
    return selectedField ? this.service.percentageFields.includes(selectedField.id) : false;
  }

  private convertPercentageForDisplay(value: number): number {
    // Multiply by 100 for display and limit to 4 decimal places
    return Math.round(value * 100 * 10000) / 10000;
  }

  private convertPercentageForStorage(value: number): number {
    // Divide by 100 for storage and limit to 4 decimal places to avoid precision issues
    return Math.round((value / 100) * 10000) / 10000;
  }

  private convertValueForDisplay(value: any, isPercentage: boolean): any {
    if (isPercentage && typeof value === 'number') {
      return this.convertPercentageForDisplay(value);
    }
    return value;
  }

  private convertValueForStorage(value: any, isPercentage: boolean): any {
    if (isPercentage && typeof value === 'number') {
      return this.convertPercentageForStorage(value);
    }
    return value;
  }

  getInputMaxLength(filterRow: FilterRow): number | null {
    const selectedField = this.getSelectedField(filterRow);
    return selectedField?.maxLength || null;
  }

  onValueChanged(filterRow: FilterRow): void {
    this.syncFilterRowToFormArray(filterRow);
  }

  private syncFilterRowToFormArray(filterRow: FilterRow): void {
    const rowIndex = this.filterRows.findIndex((row) => row.id === filterRow.id);
    if (rowIndex >= 0 && rowIndex < this.filtersFormArray.length) {
      const fieldControl = filterRow.formGroup.get('field');
      const operatorControl = filterRow.formGroup.get('operator');
      const valueControl = filterRow.formGroup.get('value');
      const isPercentage = this.isPercentageField(filterRow);

      let finalValue = valueControl?.value;

      // Convert percentage values for storage (divide by 100)
      if (finalValue !== null && finalValue !== undefined) {
        if (Array.isArray(finalValue)) {
          finalValue = finalValue.map((val) => this.convertValueForStorage(val, isPercentage));
        } else {
          finalValue = this.convertValueForStorage(finalValue, isPercentage);
        }
      }

      const filterData = {
        entityPath: [...this.service.entityPath, fieldControl?.value || null],
        filterValues: Array.isArray(finalValue) ? finalValue : [finalValue],
        operator: operatorControl?.value || null,
        filterType: BullhornEntityFiltersComponent.FILTER_TYPE,
      };

      // Find the row in filtersFormArray by matching filterId control value
      const formArrayIndex = this.filtersFormArray.controls.findIndex(
        (control) => (control as UntypedFormGroup).get('filterId')?.value === filterRow.id
      );
      if (formArrayIndex >= 0) {
        const formGroup = this.filtersFormArray.at(formArrayIndex) as UntypedFormGroup;
        formGroup.patchValue(filterData);
        formGroup.markAsDirty();
      }
    }
  }

  private resetValueControlValue(filterRow: FilterRow): void {
    const valueControl = filterRow.formGroup.get('value');
    const isMultiValue = this.isMultiValueOperator(filterRow);

    if (isMultiValue) {
      valueControl?.setValue([]);
    } else {
      valueControl?.setValue(null);
    }
  }
  //#endregion

  private loadExistingFilterRows(): void {
    // Load existing data from filtersFormArray into filterRows
    for (let i = 0; i < this.filtersFormArray.length; i++) {
      const filterControl = this.filtersFormArray.at(i) as UntypedFormGroup;
      const filterId = filterControl.get('filterId')?.value;
      const entityPath = filterControl.get('entityPath')?.value || [];
      const filterValues = filterControl.get('filterValues')?.value || [];
      const operator = filterControl.get('operator')?.value;

      if (!filterId
        || !(entityPath.length && entityPath.length > 2)
        || entityPath[1] !== this.service.entityPath[1] // compare entity name
        || !operator) {
        continue;
      }

      const rowId = filterId;
      const fieldId = entityPath.length > 0 ? entityPath[entityPath.length - 1] : null;
      const fieldMetaData = this.companyFields.find((f) => f.id === fieldId);
      const isPercentage = fieldMetaData ? this.service.percentageFields.includes(fieldMetaData.id) : false;

      // Convert percentage values for display (multiply by 100)
      let displayValues = filterValues;
      if (isPercentage && filterValues?.length) {
        displayValues = filterValues.map((val: any) => this.convertValueForDisplay(val, true));
      }

      filterControl.get('entityPath')?.setValidators([Validators.required]);
      filterControl.get('operator')?.setValidators([Validators.required]);
      filterControl.get('filterValues')?.setValidators([BullhornEntityFiltersComponent.filterValuesRequiredValidator]);
      filterControl.get('entityPath')?.updateValueAndValidity({ emitEvent: false });
      filterControl.get('operator')?.updateValueAndValidity({ emitEvent: false });
      filterControl.get('filterValues')?.updateValueAndValidity({ emitEvent: false });

      const filterRowFormGroup = new UntypedFormGroup({
        field: new UntypedFormControl(fieldId, [Validators.required]),
        operator: new UntypedFormControl(operator, [Validators.required]),
        value: new UntypedFormControl(displayValues, [BullhornEntityFiltersComponent.valueRequiredValidator]),
      });

      // If the field is a Select, determine the value correct type (single / multi) for display
      if (
        fieldMetaData?.options?.length &&
        displayValues?.length &&
        [OperatorOption.EQUALS, OperatorOption.NOT_EQUALS].includes(operator)
      ) {
        filterRowFormGroup.patchValue({ value: displayValues[0] });
      }

      const newRow: FilterRow = {
        id: rowId,
        formGroup: filterRowFormGroup,
        multiValueInputControl: new UntypedFormControl(''),
        isAddingValue: false,
      };

      this.filterRows.push(newRow);
    }
  }

  private generateRowId(): string {
    return crypto.randomUUID();
  }

  // Validates against null, empty string, or empty array
  static valueRequiredValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === undefined) {
      return { required: true };
    }
    if (Array.isArray(value)) {
      return value.length === 0 ? { required: true } : null;
    }
    if (typeof value === 'string') {
      return value === '' ? { required: true } : null;
    }
    return null;
  }

  // Validates filterValues array (always stored as an array, so [null] counts as empty)
  static filterValuesRequiredValidator(control: AbstractControl): ValidationErrors | null {
    const values: any[] = control.value;
    if (!values?.length) {
      return { required: true };
    }
    return values.every((v) => v === null || v === undefined || v === '') ? { required: true } : null;
  }
}
